import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MdOutlineDashboard,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdPayments,
} from 'react-icons/md';
import { IoStorefrontOutline } from 'react-icons/io5';
import { FaMoneyBillTransfer } from 'react-icons/fa6';
import { TfiCreditCard } from 'react-icons/tfi';
import { FiCalendar, FiHeart, FiFileText, FiGrid, FiPieChart, FiPackage, FiBriefcase, FiBarChart2, FiBookOpen, FiGift, FiCoffee, FiActivity, FiShoppingCart, FiArchive, FiSliders, FiMessageSquare, FiTruck, FiDownload, FiImage } from 'react-icons/fi';
import { BsBank } from 'react-icons/bs';
import { FEATURES } from '../../config/features';
import LogoPagaliImg from '../../assets/logo-pagali-color@2x.png';
import { useAuth } from '../../contexts/AuthContext';
import LyraChatbot from '../LyraChatbot';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  expanded: boolean;
}

interface MenuItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  permission?: string | string[];
  roles?: string[];
  requireBusinessUser?: boolean;
  showForClients?: boolean;
  requiredApp?: string;
  requiredServiceType?: string;
  featureFlag?: keyof typeof FEATURES;
}

interface MenuGroup {
  label: string;
  items: MenuItem[];
}

const NavItem: React.FC<NavItemProps> = ({
  to,
  icon,
  label,
  isActive,
  expanded,
}) => {
  return (
    <Link
      to={to}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all ${
        isActive
          ? 'bg-pagali-primary/10 text-pagali-primary font-medium'
          : 'text-pagali-gray-600 hover:bg-pagali-gray-50 hover:text-pagali-gray-900'
      } ${!expanded ? 'justify-center px-2' : ''}`}
      title={!expanded ? label : undefined}
    >
      <span className="text-lg flex-shrink-0">{icon}</span>
      {expanded && <span className="text-sm">{label}</span>}
    </Link>
  );
};

const SideBar: React.FC = () => {
  const [expanded, setExpanded] = useState(true);
  const location = useLocation();
  const { pathname } = location;
  const { user, hasPermission, isBusinessUser, hasPurchasedApp, hasServiceType } = useAuth();
  const { t } = useTranslation();

  // Menu items grouped by category
  const clientMenuGroups: MenuGroup[] = [
    {
      label: 'Principal',
      items: [
        {
          to: '/dashboard',
          icon: <MdOutlineDashboard />,
          label: t('nav.dashboard'),
          showForClients: true,
        },
        {
          to: '/hub',
          icon: <FiGrid />,
          label: t('nav.hub'),
          showForClients: true,
        },
      ],
    },
    {
      label: 'Financeiro',
      items: [
        {
          to: '/payments',
          icon: <MdPayments />,
          label: t('nav.payments'),
          showForClients: true,
        },
        {
          to: '/transactions',
          icon: <FaMoneyBillTransfer />,
          label: t('nav.transactions'),
          showForClients: true,
        },
        {
          to: '/wallet',
          icon: <TfiCreditCard />,
          label: t('nav.wallet'),
          showForClients: true,
        },
        // Banking Hub - apenas em sandbox até autorização do BCV
        {
          to: '/banking',
          icon: <BsBank />,
          label: t('nav.banking'),
          showForClients: true,
          featureFlag: 'BANKING_HUB',
        },
        {
          to: '/insights',
          icon: <FiPieChart />,
          label: t('nav.insights'),
          showForClients: true,
        },
        {
          to: '/faturali',
          icon: <FiFileText />,
          label: 'Faturali',
          showForClients: true,
          requiredApp: 'faturali',
        },
      ],
    },
    {
      label: t('nav.services'),
      items: [
        {
          to: '/ecommerce',
          icon: <IoStorefrontOutline />,
          label: t('nav.ecommerce'),
          showForClients: true,
          requiredServiceType: 'ECM',
        },
        {
          to: '/food',
          icon: <FiCoffee />,
          label: t('nav.food'),
          showForClients: true,
        },
        {
          to: '/driver',
          icon: <FiTruck />,
          label: 'Pagali Driver',
          showForClients: true,
          roles: ['PFMOT'],
        },
        {
          to: '/eventos',
          icon: <FiCalendar />,
          label: t('nav.events'),
          showForClients: true,
        },
        {
          to: '/doacoes',
          icon: <FiHeart />,
          label: t('nav.donations'),
          showForClients: true,
        },
        {
          to: '/vouchers',
          icon: <FiGift />,
          label: t('nav.vouchers'),
          showForClients: true,
          requiredServiceType: 'VCH',
        },
        {
          to: '/cashback',
          icon: <FiActivity />,
          label: 'Cashback',
          showForClients: true,
        },
        {
          to: '/meus-downloads',
          icon: <FiDownload />,
          label: 'Meus Downloads',
          showForClients: true,
        },
      ],
    },
  ];

  // Menu items for business users (Portal)
  const portalMenuItems: MenuItem[] = [
    {
      to: '/portal/dashboard',
      icon: <MdOutlineDashboard />,
      label: t('nav.portalDashboard'),
      requireBusinessUser: true,
    },
    {
      to: '/portal/payments',
      icon: <MdPayments />,
      label: user?.role === 'PFEVT'
        ? t('nav.portalSalesPasses')
        : user?.role === 'PFONG'
          ? t('nav.portalSalesDonations')
          : t('nav.portalSales'),
      requireBusinessUser: true,
    },
    {
      to: '/portal/products',
      icon: <FiPackage />,
      label: t('nav.portalProducts'),
      roles: ['PFLOJ', 'PFENT'],
      requiredServiceType: 'ECM',
    },
    {
      to: '/portal/categories',
      icon: <FiGrid />,
      label: t('nav.portalCategories'),
      roles: ['PFLOJ', 'PFENT'],
      requiredServiceType: 'ECM',
    },
    {
      to: '/portal/orders',
      icon: <FiShoppingCart />,
      label: t('nav.portalOrders'),
      roles: ['PFLOJ', 'PFENT'],
      requiredServiceType: 'ECM',
    },
    {
      to: '/portal/inventory',
      icon: <FiArchive />,
      label: t('nav.portalInventory'),
      roles: ['PFLOJ', 'PFENT'],
      requiredServiceType: 'ECM',
    },
    {
      to: '/portal/variations',
      icon: <FiSliders />,
      label: t('nav.portalVariations'),
      roles: ['PFLOJ', 'PFENT'],
      requiredServiceType: 'ECM',
    },
    {
      to: '/portal/announcements',
      icon: <FiMessageSquare />,
      label: t('nav.portalAnnouncements'),
      roles: ['PFLOJ', 'PFENT'],
      requiredServiceType: 'ECM',
    },
    {
      to: '/portal/store-profile',
      icon: <FiImage />,
      label: 'Perfil da Loja',
      roles: ['PFLOJ', 'PFENT'],
      requiredServiceType: 'ECM',
    },
    {
      to: '/portal/services',
      icon: <FiBriefcase />,
      label: t('nav.portalServices'),
      roles: ['PFEMP', 'PFGOV', 'PFPRO', 'PFENT'],
    },
    {
      to: '/portal/events',
      icon: <FiCalendar />,
      label: t('nav.portalEvents'),
      roles: ['PFEVT', 'PFENT'],
      requiredServiceType: 'EVT',
    },
    {
      to: '/portal/donations',
      icon: <FiHeart />,
      label: t('nav.portalDonations'),
      roles: ['PFONG', 'PFEVT'],
    },
    {
      to: '/portal/vouchers',
      icon: <FiGift />,
      label: t('nav.vouchers'),
      requireBusinessUser: true,
      requiredServiceType: 'VCH',
    },
    {
      to: '/portal/reports',
      icon: <FiBarChart2 />,
      label: t('nav.portalReports'),
      requireBusinessUser: true,
    },
    {
      to: '/portal/billing',
      icon: <FiFileText />,
      label: 'Faturação',
      requireBusinessUser: true,
    },
  ];

  // Filter menu items based on permissions
  const visibleMenuGroups = useMemo(() => {
    const groups: MenuGroup[] = [];

    // If user is a business user (not admin), show portal menu first
    if (isBusinessUser && user?.role !== 'PFADM') {
      const portalItems: MenuItem[] = [];
      portalMenuItems.forEach((item) => {
        if (item.permission && !hasPermission(item.permission)) return;
        if (item.roles && item.roles.length > 0) {
          if (!item.roles.includes(user?.role || '')) return;
        }
        if (item.requireBusinessUser && !isBusinessUser) return;
        if (item.featureFlag && !FEATURES[item.featureFlag]) return;
        if (item.requiredServiceType && !hasServiceType(item.requiredServiceType)) return;
        portalItems.push(item);
      });
      if (portalItems.length > 0) {
        groups.push({ label: t('nav.portal'), items: portalItems });
      }
    }

    // Add client menu groups
    clientMenuGroups.forEach((group) => {
      const filteredItems = group.items.filter((item) => {
        if (!item.showForClients) return false;
        if (item.roles && item.roles.length > 0 && !item.roles.includes(user?.role || '')) return false;
        if (item.requiredApp && !hasPurchasedApp(item.requiredApp)) return false;
        if (item.featureFlag && !FEATURES[item.featureFlag]) return false;
        if (item.requiredServiceType && !hasServiceType(item.requiredServiceType)) return false;
        return true;
      });
      if (filteredItems.length > 0) {
        groups.push({ label: group.label, items: filteredItems });
      }
    });

    return groups;
  }, [user, isBusinessUser, hasPermission, hasPurchasedApp, hasServiceType]);

  return (
    <aside
      className={`flex flex-col bg-white border-r border-pagali-gray-200 h-screen transition-all duration-300 ${
        expanded ? 'w-64' : 'w-20'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-pagali-gray-100">
        {expanded && (
          <img
            src={LogoPagaliImg}
            alt="Pagali Logo"
            className="h-10 object-contain"
          />
        )}
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-2 rounded-lg hover:bg-pagali-gray-100 text-pagali-gray-500 transition-colors"
          aria-label={expanded ? 'Recolher menu' : 'Expandir menu'}
        >
          {expanded ? (
            <MdKeyboardArrowLeft className="text-xl" />
          ) : (
            <MdKeyboardArrowRight className="text-xl" />
          )}
        </button>
      </div>

      {/* User Role Badge */}
      {expanded && isBusinessUser && user?.role !== 'PFADM' && (
        <div className="px-4 py-2 bg-pagali-primary/5 border-b border-pagali-gray-100">
          <span className="text-xs font-medium text-pagali-primary">
            Portal de Negocios
          </span>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-2 overflow-y-auto scrollbar-thin">
        {visibleMenuGroups.map((group, groupIndex) => (
          <div key={group.label}>
            {groupIndex > 0 && (
              <div className="border-t border-pagali-gray-100 mb-2" />
            )}
            {expanded && (
              <p className="px-3 mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-pagali-gray-400">
                {group.label}
              </p>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavItem
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  isActive={pathname.startsWith(item.to.replace(/\/$/, ''))}
                  expanded={expanded}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Lyra AI Assistant & Academia */}
        <div className="pt-2 mt-2 border-t border-pagali-gray-200 space-y-2">
          <LyraChatbot position="sidebar" expanded={expanded} />
          <Link
            to="/academia"
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              pathname.startsWith('/academia')
                ? 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-600 font-medium'
                : 'text-pagali-gray-600 hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50 hover:text-amber-600'
            } ${!expanded ? 'justify-center' : ''}`}
            title={!expanded ? 'Academia Pagali' : undefined}
          >
            <FiBookOpen className="text-2xl flex-shrink-0" />
            {expanded && (
              <span className="text-sm font-medium">Academia Pagali</span>
            )}
          </Link>

          {/* Promo Card */}
          {expanded && (
            <div className="mx-1 mt-3 p-4 rounded-xl bg-gradient-to-br from-pagali-primary to-purple-700 text-white">
              <p className="text-sm font-semibold leading-tight">Convide amigos e ganhe recompensas!</p>
              <p className="text-xs mt-1 opacity-80">Partilhe o Pagali e receba bonus na sua wallet.</p>
              <Link
                to="/hub?tab=referral"
                className="mt-3 inline-block w-full text-center text-xs font-semibold py-2 px-3 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
              >
                Saber mais
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Footer */}
      {expanded && (
        <div className="p-4 border-t border-pagali-gray-100 text-center">
          <p className="text-xs text-pagali-gray-500 font-medium">
            PAGALI - Cabo Verde
          </p>
          <p className="text-xs text-pagali-gray-400 mt-1">
            &copy; {new Date().getFullYear()} All Rights Reserved
          </p>
          <p className="text-xs text-pagali-gray-400 mt-1">
            Feito com <span className="text-red-500">&#9829;</span> para voce
          </p>
        </div>
      )}
    </aside>
  );
};

export default SideBar;
